/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import update from 'immutability-helper';
import {omit} from 'mad-utils/lib/shared';
import {animation} from './toast-message';

/**************************************** TYPE DEFINITIONS ****************************************/
interface ToastContainerProps {
    toastMessageFactory: Function;
    toastType?: {
        error: string;
        info: string;
        success: string;
        warning: string;
    };
    preventDuplicates?: boolean;
    newestOnTop?: boolean;
    onClick?: Function;
    className?: string;
}

interface ToastContainerState {
    toasts: any[];
    toastId: number;
    messageList: any[];
    previousMessage: string;
}

/********************************************* EXPORT *********************************************/
export class ToastContainer extends React.Component<ToastContainerProps, ToastContainerState> {
    static defaultProps = {
        toastType: {error: 'error', info: 'info', success: 'success', warning: 'warning'},
        id: 'toast-container',
        toastMessageFactory: React.createFactory(animation),
        preventDuplicates: true,
        newestOnTop: true,
        onClick() {},
    };

    state = {toasts: [], toastId: 0, messageList: [], previousMessage: null};

    error(message, title, optionsOverride) {
        this.notify(this.props.toastType.error, message, title, optionsOverride);
    }

    info(message, title, optionsOverride) {
        this.notify(this.props.toastType.info, message, title, optionsOverride);
    }

    success(message, title, optionsOverride) {
        this.notify(this.props.toastType.success, message, title, optionsOverride);
    }

    warning(message, title, optionsOverride) {
        this.notify(this.props.toastType.warning, message, title, optionsOverride);
    }

    clear() {
        Object.keys(this.refs).forEach(key => {
            (this.refs[key] as any).hideToast(false);
        });
    }

    notify(type, message, title, optionsOverride = {}) {
        if (this.props.preventDuplicates && this.state.messageList.includes(message)) return;
        const key = this.state.toastId++;
        const toastId = key;
        const newToast = update(optionsOverride, {
            $merge: {
                type,
                title,
                message,
                toastId,
                key,
                ref: `toasts__${key}`,
                handleOnClick: e => {
                    if (typeof (optionsOverride as any).handleOnClick === 'function') {
                        (optionsOverride as any).handleOnClick();
                    }
                    return this.handleToastOnClick(e);
                },
                handleRemove: this.handleToastRemove,
            },
        });
        const toastOperation = {
            [`${this.props.newestOnTop ? '$unshift' : '$push'}`]: [newToast],
        };

        const messageOperation = {
            [`${this.props.newestOnTop ? '$unshift' : '$push'}`]: [message],
        };

        const nextState = update(this.state, {
            toasts: toastOperation,
            messageList: messageOperation,
        });
        this.setState(nextState);
    }

    handleToastOnClick = event => {
        this.props.onClick(event);
        if (event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
    };

    handleToastRemove = toastId => {
        if (this.props.preventDuplicates) this.state.previousMessage = '';
        const operationName = `${this.props.newestOnTop ? 'reduceRight' : 'reduce'}`;
        this.state.toasts[operationName]((found, toast, index) => {
            if (found || toast.toastId !== toastId) return false;
            this.setState(
                update(this.state, {
                    toasts: {$splice: [[index, 1]]},
                    messageList: {$splice: [[index, 1]]},
                })
            );
            return true;
        }, false);
    };

    render() {
        const divProps = omit(this.props, [
            'toastType',
            'toastMessageFactory',
            'preventDuplicates',
            'newestOnTop',
        ]);
        return (
            <div {...divProps} aria-live="polite" role="alert">
                {this.state.toasts.map(toast => this.props.toastMessageFactory(toast))}
            </div>
        );
    }
}
